import React from 'react';
import { useSyncStatus } from '../context/SyncContext';
import { READING_WORDING } from '../domain/reading';
import s from './SyncChip.module.css';

// ── MMS has no control like this one (#169) ────────────────────────────────
//
// Every label in this file is ours. MMS has no sync indicator in its chrome,
// and nothing for one to report: no service worker, no IndexedDB, and
// localStorage holding only view preferences, so a write is always a live
// request and is never pending. Its in-flight feedback is per action — a
// drawer's Save becomes a spinner — and a successful save adds no "Saved"
// anywhere: the drawer closes and the card shows the new value.
//
// When a write fails to reach MMS's server, the drawer stays open with the
// spinner on Save and Cancel disabled, with no error text, and the edit is
// lost. Our `refused` state handles the case MMS leaves unhandled. What MMS does
// with a write its server rejects (a 4xx) is unchecked.

/**
 * What the chip says when the server refused a write (#162).
 *
 * The register of the other labels is "state · detail" — "3 pending · saved
 * locally", "Offline · 2 queued" — so this keeps it, with the part a teacher
 * must not miss first. "Not saved" rather than "Refused", because the
 * consequence is what they need, not the mechanism.
 */
export function refusedLabel(count, subject) {
  const noun = subject ? `${subject} change` : 'change';
  const plural = count === 1 ? noun : `${noun}s`;
  return `Not saved · ${count} ${plural} refused`;
}

/**
 * What the chip says about writes from a session that is gone (#167).
 *
 * Neither "refused" nor "saved": the only true thing is that the answer was
 * lost. "Check" asks the teacher to look at the rows they last edited, because
 * we cannot tell them which way it went.
 */
export function unconfirmedLabel(count) {
  return `Not confirmed · ${count} earlier ${count === 1 ? 'change' : 'changes'} — please check`;
}

/**
 * What the chip says when reads are being served from the local store with no
 * snapshot behind them this session (#207 U6).
 *
 *   * "Showing stored data", not "Showing saved data": "Saved" is a state of
 *     this chip that means a write landed.
 *   * "not refreshed yet", not "may be out of date": nothing says it is out of
 *     date, only that nothing has confirmed it is current, so the count is what
 *     it says.
 *
 * MMS has no read of its own that can be stale in this sense (see the header).
 */
export function staleLabel(count) {
  return `Showing stored data · ${count} ${count === 1 ? 'list' : 'lists'} not refreshed yet`;
}

/**
 * One dead batch, as a sentence a teacher can decide on (#206): how many
 * documents, what they are, how many attempts were spent, and which terminal
 * state this is. `refused` and `exhausted` stay distinct (#125) because a
 * teacher acts on them differently — the server said no, against nobody ever
 * answering.
 *
 * `attempts` appears only on the exhausted half: on a refusal it is always 1,
 * and printing it would suggest that trying harder was the problem.
 */
export function deadLetterLabel({ subject, docCount, attempts, reason }) {
  const noun = subject || 'change';
  const what = `${docCount} ${docCount === 1 ? noun : `${noun}s`}`;
  return reason === 'exhausted'
    ? `${what} · no answer after ${attempts} ${attempts === 1 ? 'attempt' : 'attempts'}`
    : `${what} · the server refused it`;
}

/**
 * Documents a dead batch left a hole in the history of (#206).
 *
 * The core reports a causal gap and refuses to resolve one: a batch queued
 * behind a dead one on the same document is not held back — that would put one
 * poisoned document in front of a whole studio — so a later edit may have
 * landed on a document whose creation never did. The core can see the order
 * was broken, not whether it mattered, so this says "may" rather than "was"
 * and names the blind spot.
 */
export function causalGapLabel(count) {
  const records = `${count} ${count === 1 ? 'record' : 'records'}`;
  return `Later changes to ${records} went out after this failed, so they may have been `
    + 'applied out of order. Nothing here can tell whether it mattered — worth a look.';
}

/**
 * Work this device is holding for a studio that is not signed in (#300).
 *
 * A sign-out parks a write that is still owed (#283). If that teacher never
 * signs in on this browser again, the batch stays in `coda-write-queue` for the
 * life of the origin, counted for nobody, while they believe it was saved.
 * A teacher who reads this on a shared laptop knows to let the colleague whose
 * work it is sign in once, which is the only thing that sends it.
 *
 * It must not look like the reader's own work is unsaved: the subject is the
 * device, the work is not described, no count of the signed-in teacher's own
 * writes is involved, and there is no control beside it, since there is nothing
 * the reader can do. MMS has no offline queue, so there is no precedent.
 */
export function foreignOwedLabel(count, studios) {
  const changes = `${count} unsent ${count === 1 ? 'change' : 'changes'}`;
  const whose = studios === 1 ? 'another account' : `${studios} other accounts`;
  return `This device is still holding ${changes} for ${whose}. `
    + 'They will be sent when that account signs in here.';
}

export default function SyncChip() {
  const {
    syncState,
    pendingCount,
    refusedCount,
    refusedSubject,
    dismissRefusals,
    retryDeadLetters,
    unconfirmedCount,
    dismissUnconfirmed,
    staleReadCount,
  } = useSyncStatus();

  // `pendingCount` is rendered in three of the labels below. It is the
  // durable notes' count (`outstandingWrites.js`, #167, #169): every intended
  // write outlives the tab, for every collection, with no listeners.
  const configs = {
    clean: {
      label: 'Up to date',
      className: s.clean,
      dot: true,
    },
    pending: {
      label: `${pendingCount} pending · saved locally`,
      className: s.pending,
      dot: true,
      pulse: false,
    },
    syncing: {
      label: pendingCount > 0
        ? `Syncing ${pendingCount} ${pendingCount === 1 ? 'change' : 'changes'}…`
        : 'Syncing…',
      className: s.syncing,
      spinner: true,
    },
    saved: {
      label: 'Saved',
      className: s.saved,
      check: true,
    },
    offline: {
      label: `Offline${pendingCount > 0 ? ` · ${pendingCount} queued` : ''}`,
      className: s.offline,
      icon: 'wifi-off',
    },
    // No dismissal and no timer: `stale` is a condition, not an event, and it
    // ends when a snapshot lands. syncMachine.js has why it sits under
    // `offline` and over `pending`.
    stale: {
      label: staleLabel(staleReadCount),
      className: s.stale,
      icon: 'clock',
    },
    // Some list has heard nothing from the server on this device and holds
    // nothing (#651, #682): the screen says *Not loaded on this device yet*,
    // and the chip agrees rather than saying "Up to date". The core's words
    // (`awaitingChip`), which do not say "offline": the device may be online
    // with the server unreachable. Ranked above `stale` by
    // `core/src/sync_chip.rs`. A condition, so no dismissal and no timer, and
    // `stale`'s look: both say the screen is not the server's latest.
    awaiting: {
      label: READING_WORDING.awaitingChip,
      className: s.stale,
      icon: 'clock',
    },
    unconfirmed: {
      label: unconfirmedLabel(unconfirmedCount),
      className: s.refused,
      icon: 'alert',
      // Like `refused`, no timer ends it, so it needs a way out that is not
      // waiting; it shares the styling, and the sentence distinguishes them.
      dismiss: true,
      alert: true,
      onDismiss: 'unconfirmed',
    },
    refused: {
      label: refusedLabel(refusedCount, refusedSubject),
      className: s.refused,
      icon: 'alert',
      // The only state with controls, because it does not end on a timer (see
      // src/lib/syncMachine.js): a teacher has lost an edit.
      //
      // "Try again" beside Dismiss (#206): `queue.retry` puts the batch back
      // at the front of its own documents with the budget restored, and it
      // cannot lose anything — at worst the server says no again and this
      // comes back.
      //
      // Discard (`SyncContext.discardDeadLetter`) is not beside the dismiss,
      // where one wrong click of two adjacent buttons would lose work unseen.
      // It is in the list, per batch, next to a row that says what that batch
      // is. The two controls up here cannot lose anything.
      dismiss: true,
      retry: true,
      alert: true,
      // The list is `SyncNotices` below, under the topbar: the topbar is a
      // fixed-height row inside `.main-area { overflow: hidden }`, with no room
      // for a disclosure to expand.
    },
  };

  const config = configs[syncState] || configs.clean;

  return (
  <div className={config.className} role={config.alert ? 'alert' : undefined}>
      {config.icon === 'alert' && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v6" />
          <path d="M12 16.5h.01" />
        </svg>
      )}
      {config.spinner && (
        <svg className={s.spinner} viewBox="0 0 16 16">
          <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor"
            strokeWidth="2" strokeDasharray="22 12" strokeLinecap="round" />
        </svg>
      )}
      {config.icon === 'wifi-off' && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
          strokeLinejoin="round">
          <path d="M3 3l18 18" />
          <path d="M16.7 16.7A5 5 0 0 0 12 14" />
          <path d="M5 12.5a9 9 0 0 1 5-2.5" />
          <path d="M2 8.8a14 14 0 0 1 4.2-2.4" />
          <path d="M19.8 13.4A14 14 0 0 0 22 8.8" />
          <path d="M12 20h.01" />
        </svg>
      )}
      {config.icon === 'clock' && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
          strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      )}
      {config.check && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          strokeLinejoin="round">
          <path d="M4 12l5 5 11-12" />
        </svg>
      )}
      {config.dot && <span className={s.dot} />}
      {config.label}
      {config.retry && (
        <button
          type="button"
          className={s.action}
          // Fire and forget: the answer arrives as a state of this chip
          // either way — the write lands and the count falls, or it dies
          // again and `onWriteRefused` says so. Two-argument `then` because
          // the rejection handler is for the retry's own failure.
          onClick={() => retryDeadLetters().then(undefined, (err) => {
            console.error('[sync] retrying the dead letters failed:', err);
          })}
        >
          Try again
        </button>
      )}
      {config.dismiss && (
        <button
          type="button"
          className={s.dismiss}
          onClick={config.onDismiss === 'unconfirmed' ? dismissUnconfirmed : dismissRefusals}
          aria-label="Dismiss"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            strokeLinejoin="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * #206's list and #300's note, under the topbar rather than in it.
 *
 * `SyncChip` is mounted by `AppShell` inside `<header className="topbar">`, a
 * fixed-height flex row inside `.main-area { overflow: hidden }`, so a list in
 * it would have nowhere to expand. It goes where `TrialBanner` goes, for the
 * reason `AppShell` gives about that banner. The styling is `.panel`,
 * `.summary`, `.row`, `.rowAction`, `.rowDiscard`, `.gapNote` and `.deviceNote`
 * in `SyncChip.module.css` (#325); `test/ui/deadLetterSurface.test.jsx` covers
 * the behaviour.
 *
 * Gated on `refused`: `Dismiss` on the chip means "stop telling me", and a panel
 * that outlived the sentence would make that button a half-measure.
 */
export function SyncNotices() {
  const {
    syncState,
    deadLetters,
    retryDeadLetter,
    discardDeadLetter,
    causalGaps,
    foreignOwedCount,
    foreignOwedStudios,
  } = useSyncStatus();

  const showList = syncState === 'refused' && deadLetters.length > 0;
  // Nothing to say: `null` rather than an empty strip, which would be a
  // permanent extra line under the header of every screen.
  if (!showList && foreignOwedCount === 0) return null;

  return (
    <div className={s.notices}>
      {showList && (
        <details className={s.panel}>
          <summary className={s.summary}>
            {deadLetters.length === 1
              ? 'The change that could not be saved'
              : `The ${deadLetters.length} changes that could not be saved`}
          </summary>
          <ul className={s.noticeList}>
            {deadLetters.map((letter) => (
              <li className={s.row} key={letter.batchId}>
                <span className={s.rowText}>{deadLetterLabel(letter)}</span>
                <button
                  type="button"
                  className={s.rowAction}
                  // Fire and forget, like the chip's own retry: every outcome
                  // arrives back as a state of the chip. Two-argument `then`
                  // because the rejection handler is for the retry's own
                  // failure and not for a bug in the handler.
                  onClick={() => retryDeadLetter(letter.batchId).then(undefined, (err) => {
                    console.error('[sync] retrying a dead letter failed:', err);
                  })}
                >
                  {/*
                    "Send again" rather than the chip's "Try again": one puts
                    every dead batch back, this one this batch, and two buttons
                    reading the same on one screen would leave a teacher
                    guessing which.
                  */}
                  Send again
                </button>
                <button
                  type="button"
                  className={s.rowDiscard}
                  onClick={() => discardDeadLetter(letter.batchId).then(undefined, (err) => {
                    console.error('[sync] discarding a dead letter failed:', err);
                  })}
                >
                  Discard
                </button>
              </li>
            ))}
          </ul>
          {causalGaps.length > 0 && <p className={s.gapNote}>{causalGapLabel(causalGaps.length)}</p>}
        </details>
      )}

      {/*
        #300. Outside the state machine: a fact about the device, not about
        the signed-in teacher's writes, so it is true while the chip says "Up
        to date" — the session it needs to be visible in. No control: see
        `foreignOwedLabel`.
      */}
      {foreignOwedCount > 0 && (
        <p className={s.deviceNote}>{foreignOwedLabel(foreignOwedCount, foreignOwedStudios)}</p>
      )}
    </div>
  );
}
